import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import type { AuditEvent, AuditFilters, AuditListResponse } from "../types";

const KEY = ["audit"] as const;

function buildQuery(
  filters: AuditFilters,
): Record<string, string | number | undefined | null> {
  return {
    category: filters.category,
    severity: filters.severity,
    status: filters.status,
    module: filters.module,
    performedById: filters.performedById,
    companyId: filters.companyId,
    productId: filters.productId,
    projectId: filters.projectId,
    search: filters.search,
    preset: filters.preset,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    page: filters.page,
    limit: filters.limit,
  };
}

export function useAuditEvents(
  filters: AuditFilters,
): UseQueryResult<AuditListResponse> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...KEY, "list", tokenKey, filters],
    enabled: !!accessToken,
    staleTime: 10_000,
    queryFn: async () => {
      return await apiFetch<AuditListResponse>("/audit", {
        token: accessToken,
        query: buildQuery(filters),
      });
    },
  });
}

export function useAuditRecord(
  entityType: string | null | undefined,
  entityId: string | null | undefined,
  enabled = true,
): UseQueryResult<{ data: AuditEvent[]; total: number }> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...KEY, "record", tokenKey, entityType, entityId],
    enabled: !!accessToken && !!entityType && !!entityId && enabled,
    staleTime: 0,
    queryFn: async () => {
      return await apiFetch<{ data: AuditEvent[]; total: number }>(
        `/audit/record/${entityType}/${entityId}`,
        { token: accessToken },
      );
    },
  });
}

export function buildExportUrl(filters: AuditFilters): string {
  const base = (
    import.meta.env.VITE_API_URL ?? 
    (import.meta.env.DEV ? "http://localhost:3000/api" : "/api")
  ).replace(/\/$/, "");
  const params = new URLSearchParams();
  const q = buildQuery(filters);
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  return `${base}/audit/export${qs ? `?${qs}` : ""}`;
}
